import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import { Target, Users, Award, Heart } from 'lucide-react';

export default function AboutPage() {
  const values = [
    { icon: Target, title: 'Reliability', description: 'We deliver on time, every time. Our commitment to punctuality and dependable service sets us apart.' },
    { icon: Heart, title: 'Customer First', description: 'Your satisfaction is our priority. We go above and beyond to ensure a seamless experience.' },
    { icon: Award, title: 'Quality Service', description: 'Professional, clean equipment and expert guidance for every project, big or small.' },
    { icon: Users, title: 'Community Focus', description: 'We are locally owned and operated, committed to serving our community responsibly.' },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative pt-32 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?auto=format&fit=crop&w=2000&q=80" alt="Professional team" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-hero"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-6 bg-accent text-accent-foreground border-0">15+ Years of Excellence</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-background mb-6 leading-tight">About DumpsterRental</h1>
            <p className="text-lg sm:text-xl text-background/90 leading-relaxed">
              Your trusted partner in waste management since 2009. We're committed to providing reliable, eco-friendly solutions that make your projects easier.
            </p>
          </div>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-8 text-center">Our Story</h2>
            <div className="space-y-6 text-muted-foreground leading-relaxed">
              <p className="text-base">
                Founded in 2009 by John Davis, DumpsterRental started with a simple mission: to provide reliable, affordable dumpster rental services to our local community. What began with just 10 dumpsters and a single truck has grown into a full-service waste management company serving over 50 cities.
              </p>
              <p className="text-base">
                Over the years, we've built our reputation on three core principles: reliability, transparency, and exceptional customer service. We understand that whether you're a homeowner tackling a renovation or a contractor managing a construction site, you need a waste management partner you can count on.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 md:py-24 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Our Core Values</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">The principles that guide everything we do</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-border hover:shadow-elegant transition-smooth group">
                  <CardContent className="pt-6 text-center">
                    <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4 mx-auto group-hover:bg-gradient-primary transition-smooth">
                      <Icon className="w-7 h-7 text-primary group-hover:text-primary-foreground transition-smooth" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">{value.title}</h3>
                    <p className="text-sm text-muted-foreground">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">Ready to Experience the Difference?</h2>
            <p className="text-lg text-muted-foreground mb-8">Join thousands of satisfied customers who trust us with their waste management needs.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-gradient-primary hover:shadow-glow transition-smooth text-base">
                <Link to="/contact">Get Started</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base">
                <Link to="/services">View Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
